import { useEffect, useState } from 'react';

export const useIsOverflow = (
    ref: React.MutableRefObject<HTMLElement>,
    scrollRef: React.MutableRefObject<HTMLElement>,
    forceReflow: unknown = null
) => {
    const [refYOverflowing, setIsOverflow] = useState(false);
    const [refYScrollBegin, setRefYScrollBegin] = useState(true);
    const [refYScrollEnd, setRefYScrollEnd] = useState(false);

    const [refXOverflowing, setXIsOverflow] = useState(false);
    const [refXScrollBegin, setRefXScrollBegin] = useState(true);
    const [refXScrollEnd, setRefXScrollEnd] = useState(false);

    useEffect(() => {
        if (!ref?.current && !scrollRef?.current) {
            return;
        }

        const { current } = ref;
        const { current: scrollCurrent } = scrollRef;

        const trigger = () => {
            const hasOverflow =
                scrollCurrent.scrollHeight > scrollCurrent.clientHeight;
            const offsetBottom =
                scrollCurrent.scrollHeight - scrollCurrent.clientHeight;
            setRefYScrollEnd(scrollCurrent.scrollTop >= offsetBottom);
            setRefYScrollBegin(scrollCurrent.scrollTop === 0);
            setIsOverflow(hasOverflow);

            const hasXOverflow =
                scrollCurrent.scrollWidth > scrollCurrent.clientWidth;
            const offsetRight =
                scrollCurrent.scrollWidth - scrollCurrent.clientWidth;
            setRefXScrollEnd(scrollCurrent.scrollLeft >= offsetRight - 5);
            setRefXScrollBegin(scrollCurrent.scrollLeft === 0);
            setXIsOverflow(hasXOverflow);
        };

        let resizeObserver: ResizeObserver | null;

        if ('ResizeObserver' in window) {
            resizeObserver = new ResizeObserver(trigger);
            resizeObserver.observe(current);
        }

        scrollCurrent.addEventListener('scroll', trigger);

        trigger();

        return () => {
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            scrollCurrent.removeEventListener('scroll', trigger);
        };
    }, [ref, forceReflow]);

    return {
        refYOverflowing,
        refYScrollBegin,
        refYScrollEnd,
        refXOverflowing,
        refXScrollBegin,
        refXScrollEnd,
    };
};
